import { useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import ObjectID from 'bson-objectid';
import { useSetWelcomeMessage } from 'hooks/use-type-welcome-message';
import { useTranslation } from 'react-i18next';
import { ReplaySubject, Subject } from 'rxjs';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { NotificationService } from 'services/notification-service';
import { PosthogEvent, PosthogService } from 'services/posthog-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { USER_SMART_ASSISTANT_CONVERSATIONS_QUERY_KEY } from 'shared/constants/query-keys';
import {
  AskDto,
  ChatMessage,
  EmailHandoffToolEventData,
  ForcedToolInvocation,
  SmartAssistantCompleteResponse,
  SmartAssistantMessageEvent,
  SmartAssistantMessageEventType,
  SmartAssistantToolEvent,
  ToolEventType
} from 'shared/interfaces/ChatMessage';
import { Conversation, ConversationSourceType, CouversationSource } from 'shared/interfaces/Conversation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { ToolKind } from 'shared/interfaces/tools/Tool';
import { transformChatMessageWithPartialResponse } from 'shared/utils/smart-assistant';

interface ActiveConversation {
  id: string;
  isNew: boolean;
}

interface ChatMessageHandlingProps {
  type: 'DEFAULT' | 'GUEST' | 'RESOURCE';
  smartAssistant?: SmartAssistant;
  resourceId?: string;
  initialConversation?: Conversation;
}

export function useChatMessageHandling({
  type,
  smartAssistant,
  resourceId,
  initialConversation
}: ChatMessageHandlingProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<ChatMessage[]>(initialConversation?.messages ?? []);
  const [activeConversation, setActiveConversation] = useState<ActiveConversation>({
    id: initialConversation?._id ?? new ObjectID().toString(),
    isNew: !initialConversation
  });
  const eventSubject = useRef(new Subject<SmartAssistantMessageEvent>()).current;

  useEffect(() => {
    const eventSubscription = eventSubject.subscribe((event) => {
      // For now we only need to process tool events
      // We can add more event types in the future and process them here
      // For example, the sources event can be processed here
      if (event.type === SmartAssistantMessageEventType.TOOL_EVENT)
        appendToolEventMessage(event.data as SmartAssistantToolEvent);
    });

    return () => {
      eventSubscription.unsubscribe();
    };
  }, [eventSubject]);

  useSetWelcomeMessage(setMessages, smartAssistant);

  const sendMessageMutation = useMutation({
    mutationFn: (params: AskDto) => {
      const { smartAssistantId, resourceId, conversationInfo, forcedToolInvocation, partialResponseSubject } = params;

      // Capture chat message event in PostHog
      PosthogService.capture(PosthogEvent.SMART_ASSISTANT_MESSAGE_SENT, { smartAssistantId, conversationType: type });

      if ((type === 'DEFAULT' || type === 'GUEST') && smartAssistantId) {
        return (type === 'DEFAULT' ? SmartAssistantService : GuestSmartAssistantService).askSmartAssistant(
          {
            smartAssistantId,
            conversationInfo: {
              ...conversationInfo,
              messages: undefined
            },
            forcedToolInvocation
          },
          eventSubject,
          partialResponseSubject
        );
      } else if (type === 'RESOURCE' && resourceId && conversationInfo.messages) {
        return SmartAssistantService.askAboutResource(
          {
            resourceId,
            conversation: conversationInfo.messages.map((message) => transformChatMessageWithPartialResponse(message))
          },
          eventSubject,
          partialResponseSubject
        );
      }

      // If the type is not defined, return an empty promise
      return Promise.resolve([] as SmartAssistantMessageEvent[]);
    },
    onSuccess: (finalOrderedEvents: SmartAssistantMessageEvent[] | undefined, variables: AskDto) => {
      if (!smartAssistant || type !== 'DEFAULT') return;

      if (variables.isNewConversation) {
        // We need to fetch the new conversation
        queryClient.invalidateQueries([
          USER_SMART_ASSISTANT_CONVERSATIONS_QUERY_KEY,
          { smartAssistantId: smartAssistant._id }
        ]);
      } else {
        // If the conversation already exists, update the conversation in the cache
        queryClient.setQueryData(
          [USER_SMART_ASSISTANT_CONVERSATIONS_QUERY_KEY, { smartAssistantId: smartAssistant?._id }],
          (existingConversations: Conversation[] = []) =>
            existingConversations.map((existingConversation) => {
              const newMessages = [];

              // Append the user message
              if (variables.conversationInfo.lastMessage) {
                newMessages.push(variables.conversationInfo.lastMessage);
              }

              // Append the smart assistant response and tool events
              for (const event of finalOrderedEvents ?? []) {
                if (event.type === SmartAssistantMessageEventType.COMPLETE_RESPONSE) {
                  newMessages.push({ text: (event.data as SmartAssistantCompleteResponse).text, fromUser: false });
                } else if (event.type === SmartAssistantMessageEventType.TOOL_EVENT) {
                  newMessages.push({ text: '', fromUser: false, toolEvent: event.data as SmartAssistantToolEvent });
                }
              }

              return existingConversation._id === activeConversation?.id
                ? { ...existingConversation, messages: [...existingConversation.messages, ...newMessages] }
                : existingConversation;
            })
        );
      }
    },
    onError: (error: AxiosError) => {
      PosthogService.capture(PosthogEvent.ERROR_CHAT_MESSAGE_HANDLING, {
        errorMessage: error.message,
        errorCode: error.code,
        errorRequest: error.request,
        errorResponse: error.response
      });
      NotificationService.showResult(false, 'SMART_ASSISTANT.ERROR', error, type === 'GUEST');
    }
  });

  const appendToolEventMessage = (toolEvent: SmartAssistantToolEvent) =>
    addNewMessageToConversation({ text: '', fromUser: false, toolEvent });

  const addNewMessageToConversation = (
    newMessage: ChatMessage,
    partialResponseSubject?: ReplaySubject<SmartAssistantMessageEvent>
  ) => {
    // Add the user's new message and a new empty message for the smart assistant
    setMessages((currentMessages) =>
      partialResponseSubject
        ? [...currentMessages, newMessage, { text: '', fromUser: false, partialResponseSubject }]
        : [...currentMessages, newMessage]
    );
  };

  const sendMessage = (chatMessage: ChatMessage) => {
    const newUserMessage: ChatMessage = {
      text: chatMessage.text,
      fromUser: true,
      attachments: chatMessage.attachments
    };

    const { id, isNew } = activeConversation;

    const partialResponseSubject = new ReplaySubject<SmartAssistantMessageEvent>();

    sendMessageMutation.mutate({
      conversationInfo: {
        // Both lastMessage and messages are sent here because of the unified mutation
        // The mutation will decide which one to use based on type (DEFAULT or RESOURCE)
        lastMessage: newUserMessage,
        messages: [...messages, newUserMessage],
        conversationId: id,
        source: getConversationSource(),
        title: type === 'DEFAULT' && !activeConversation?.id ? t('SMART_ASSISTANT.NEW_CONVERSATION') : undefined
      },
      smartAssistantId: smartAssistant?._id,
      resourceId,
      isNewConversation: isNew,
      partialResponseSubject
    });

    if (isNew) setActiveConversation({ id, isNew: false });

    addNewMessageToConversation(newUserMessage, partialResponseSubject);
  };

  const setNewConversation = () => {
    if (sendMessageMutation.isLoading) return;
    setActiveConversation({ id: new ObjectID().toString(), isNew: true });
    setMessages(smartAssistant?.welcomeMessage ? [{ text: smartAssistant?.welcomeMessage, fromUser: false }] : []);
  };

  const forceToolInvocation = (forcedToolInvocation: ForcedToolInvocation) => {
    const { config } = forcedToolInvocation;
    if (config.kind === ToolKind.COMPLETE_EMAIL_HANDOFF && activeConversation?.id) {
      setMessages((currentMessages) => {
        const messagesTemp = [...currentMessages];
        for (const message of messagesTemp) {
          if (message.toolEvent?.type === ToolEventType.EMAIL_HANDOFF && message.toolEvent.data === undefined) {
            const { email, name, phone, reason } = config;
            message.toolEvent.data = { email, name, phone, reason } as EmailHandoffToolEventData;
          }
        }

        const partialResponseSubject = new ReplaySubject<SmartAssistantMessageEvent>();

        const { id, isNew } = activeConversation;

        sendMessageMutation.mutate({
          conversationInfo: {
            messages: [],
            conversationId: id,
            source: getConversationSource()
          },
          smartAssistantId: smartAssistant?._id,
          resourceId,
          forcedToolInvocation: {
            config: {
              ...config,
              conversationId: id
            }
          },
          isNewConversation: isNew,
          partialResponseSubject
        });

        return [...messagesTemp, { text: '', fromUser: false, partialResponseSubject }];
      });
    }
  };

  const getConversationSource = () => {
    const isEmbedded = window.self !== window.top;

    return {
      type:
        type === 'DEFAULT'
          ? ConversationSourceType.APP
          : isEmbedded
          ? ConversationSourceType.WEBSITE
          : ConversationSourceType.LINK
    } as CouversationSource;
  };

  return {
    messages,
    setMessages,
    sendMessage,
    setNewConversation,
    addNewMessageToConversation,
    forceToolInvocation,
    isStreamingResponse: sendMessageMutation.isLoading, // The mutation is loading until the promise is resolved (when we get the stream end event)
    activeConversation: activeConversation,
    setActiveConversationId: (conversationId?: string) =>
      setActiveConversation({
        id: conversationId ?? new ObjectID().toString(),
        isNew: !conversationId
      })
  };
}
